import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useAuthActions, useAuthState } from "@/store/auth";
import { useUserStore } from "@/store/user";
import { getUserName, getUserAttribute } from "@/utils/workos";
import { getUserPicture } from "@/utils/user";
import env from "@/env";
import type { MenuItem, Tool } from "@/types";
import { TOOL_OPTIONS } from "@/constants/tool";
import { trackEvent } from "@/utils/analytics";
import { explorerPrefixedPath, isCloudPrefixedIdPath } from "@/utils";
import { isExplorerOnlyMode } from "@/utils/explorerOnlyMode";
import { useMainStore } from "@/store/main";
import { usePaginatedUserNotificationStore } from "@/store/paginated";
import { LogOutIcon } from "@/components/Icons/LogOutIcon";
import { Bell } from "lucide-react";
import { BalanceType } from "@/types/user";
import { toLocaleString } from "@/utils/number";
import { AnalyticsEventType } from "@/constants/analytics";
import { getIsCreditsEnabled } from "@/utils/credits";
import { RequestCreditsModal } from "@/components/Common/RequestCreditsModal/RequestCreditsModal";
import { PurchaseCreditsModal } from "@/components/Common/PurchaseCreditsModal/PurchaseCreditsModal";
import { SupportModal } from "@/components/Common/SupportModal/SupportModal";
import { QuestionIcon } from "@/components/Icons/QuestionIcon";

import { SidebarChevronIcon } from "@/components/Icons/SidebarChevronIcon";
import { SidebarLogoIcon } from "@/components/Icons/SidebarLogoIcon";
import { SidebarDashboardIcon } from "@/components/Icons/SidebarDashboardIcon";
import { SidebarGetStarted } from "@/components/Common/LeftSidebar/SidebarGetStarted";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";


/** Tool `value`s shown in this sidebar, in order — resolved from `getToolOptions()` like the rest of the app. */
const LEFT_SIDEBAR_TOOL_ORDER = ["cloud", "ai"] as const;

function toolRoutePrefix(tool: Tool): string {
  if (tool.prefix) {
    return tool.prefix;
  }
  const parts = tool.href.split("/").filter(Boolean);
  return parts.length > 0 ? `/${parts[0]}` : tool.href;
}

function isCloudDashboardPath(location: string): boolean {
  return (
    location.startsWith("/cloud/dashboard") ||
    location.startsWith("/explorer/dashboard") ||
    location === "/dashboard"
  );
}

function isSidebarToolSectionActive(tool: Tool, location: string): boolean {
  const routePrefix = toolRoutePrefix(tool);
  if (!location.startsWith(routePrefix)) {
    return false;
  }
  if (tool.value === "cloud" && (isCloudDashboardPath(location) || isCloudPrefixedIdPath(location))) {
    return false;
  }
  return true;
}

/** Same visibility rules as `HorizontalMenu` (delayed login + requiresLogin). */
function getAllowedMenuItems(items: MenuItem[], isLoggedIn: boolean): MenuItem[] {
  if (env.FEATUREFLAG_DELAYED_LOGIN && !isLoggedIn) {
    return [...items]
      .filter(({ requiresLogin, showInDelayedLogin }) => {
        if (showInDelayedLogin) {
          return true;
        }
        return requiresLogin !== true;
      })
      .sort((a, b) => {
        if (isLoggedIn) {
          return 0;
        }
        if (a.showInDelayedLogin && !b.showInDelayedLogin) {
          return 1;
        }
        if (!a.showInDelayedLogin && b.showInDelayedLogin) {
          return -1;
        }
        return 0;
      });
  }
  return items;
}

/** Leaf links under a group (allowed + has href + not disabled). */
function getVisibleSubMenuItems(items: MenuItem[], isLoggedIn: boolean): MenuItem[] {
  return getAllowedMenuItems(items, isLoggedIn).filter((m) => Boolean(m.href) && !m.disabled);
}

/** Nav links under a tool — same filter as the expanded sidebar accordion (excludes Cloud “Home” duplicate). */
function getSidebarToolNavItems(
  tool: Tool,
  cloudTool: Tool | undefined,
  homeMenuItem: MenuItem | undefined,
  isLoggedIn: boolean
): MenuItem[] {
  return getAllowedMenuItems(tool.menuItems ?? [], isLoggedIn).filter((m) => {
    if (homeMenuItem && cloudTool && tool.value === cloudTool.value && m.value === homeMenuItem.value) {
      return false;
    }
    if (m.subMenuItems?.length) {
      return getVisibleSubMenuItems(m.subMenuItems, isLoggedIn).length > 0;
    }
    if (!m.href || m.disabled) {
      return false;
    }
    return true;
  });
}

function flattenSidebarLeafNavItems(navItems: MenuItem[], isLoggedIn: boolean): MenuItem[] {
  const leaves: MenuItem[] = [];
  for (const item of navItems) {
    if (item.subMenuItems?.length) {
      leaves.push(...getVisibleSubMenuItems(item.subMenuItems, isLoggedIn));
    } else if (item.href && !item.disabled) {
      leaves.push(item);
    }
  }
  return leaves;
}

/** Best-matching nav item for the current path (longest `href` prefix wins). */
function getActiveSidebarNavItem(
  navItems: MenuItem[],
  location: string,
  isLoggedIn: boolean
): MenuItem | null {
  let active: MenuItem | null = null;
  let activeHrefLen = 0;
  for (const item of flattenSidebarLeafNavItems(navItems, isLoggedIn)) {
    const href = item.href as string;
    if (!location.startsWith(href) || href.length <= activeHrefLen) {
      continue;
    }
    active = item;
    activeHrefLen = href.length;
  }
  return active;
}

/** Submenu row — icon is `item.icon` from the tool’s `menuItems` array only (disabled entries are filtered out). */
function SidebarToolMenuRow({
  item,
  location,
  registerRowRef,
  nested = false,
}: {
  item: MenuItem;
  location: string;
  registerRowRef: (id: string) => (el: HTMLAnchorElement | null) => void;
  nested?: boolean;
}) {
  const href = item.href as string;
  const isActive = location.startsWith(href);
  const ItemIcon = item.icon;
  const rowClass = cn(
    "group relative flex w-full min-w-0 items-center gap-2 rounded-lg py-2 text-sm font-medium transition-colors hover:bg-gray-light-500 dark:hover:bg-border",
    nested ? "pl-6 pr-3" : "px-3",
    isActive ? "text-foreground" : "text-gray-dark-2200 hover:text-foreground"
  );
  return (
    <Link href={href} asChild>
      <a
        ref={registerRowRef(item.value)}
        className={rowClass}
        onClick={() => {
          if (item.analyticEvent) {
            trackEvent(item.analyticEvent, { tab: item.label });
          }
        }}
      >
        <div className="relative flex size-[16px] shrink-0 items-center justify-center overflow-hidden [&_svg]:h-full [&_svg]:w-full [&_svg]:max-h-full [&_svg]:max-w-full">
          {ItemIcon ? <ItemIcon className="size-full shrink-0" /> : null}
        </div>
        {item.label}
      </a>
    </Link>
  );
}

/** Expanded tool submenu: single blue accent bar that slides vertically to the active row. */
function SidebarToolSubmenuList({
  navItems,
  location,
  isLoggedIn,
}: {
  navItems: MenuItem[];
  location: string;
  isLoggedIn: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLAnchorElement>>(new Map());
  const rowRefCallbacksRef = useRef<Map<string, (el: HTMLAnchorElement | null) => void>>(new Map());
  const [indicator, setIndicator] = useState<{ top: number; height: number; visible: boolean }>({
    top: 0,
    height: 0,
    visible: false,
  });
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const item of navItems) {
      if (!item.subMenuItems?.length) {
        continue;
      }
      const subs = getVisibleSubMenuItems(item.subMenuItems, isLoggedIn);
      if (subs.some((sub) => location.startsWith(sub.href as string))) {
        initial.add(item.value);
      }
    }
    return initial;
  });

  const activeKey = useMemo(() => {
    for (const item of flattenSidebarLeafNavItems(navItems, isLoggedIn)) {
      const h = item.href as string;
      if (location.startsWith(h)) {
        return item.value;
      }
    }
    return null;
  }, [navItems, location, isLoggedIn]);

  const navSignature = useMemo(() => navItems.map((i) => i.value).join(","), [navItems]);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      for (const item of navItems) {
        if (!item.subMenuItems?.length) {
          continue;
        }
        const subs = getVisibleSubMenuItems(item.subMenuItems, isLoggedIn);
        if (subs.some((sub) => location.startsWith(sub.href as string))) {
          next.add(item.value);
        }
      }
      return next;
    });
  }, [location, navItems, isLoggedIn]);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container || !activeKey) {
      setIndicator((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    const el = rowRefs.current.get(activeKey);
    if (!el) {
      return;
    }
    const c = container.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setIndicator({
      top: r.top - c.top,
      height: r.height,
      visible: true,
    });
  }, [activeKey]);

  useLayoutEffect(() => {
    measure();
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [measure, location, navSignature]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return;
    }
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    return () => ro.disconnect();
  }, [measure, navSignature]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  const registerRowRef = useCallback((id: string) => {
    let cb = rowRefCallbacksRef.current.get(id);
    if (!cb) {
      cb = (el: HTMLAnchorElement | null) => {
        if (el) {
          rowRefs.current.set(id, el);
        } else {
          rowRefs.current.delete(id);
        }
      };
      rowRefCallbacksRef.current.set(id, cb);
    }
    return cb;
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative ml-[1px] mr-0 flex w-full min-w-0 flex-col gap-0.5 border-l border-border pl-[10px]"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 w-[1.5px] rounded-full bg-[#3179ff] transition-[top,height,opacity] duration-300 ease-[cubic-bezier(0.33,1,0.68,1)]"
        style={{
          top: indicator.top,
          height: indicator.visible ? indicator.height : 0,
          opacity: indicator.visible ? 1 : 0,
        }}
      />
      {navItems.map((item) => {
        if (item.subMenuItems?.length) {
          const visibleSubs = getVisibleSubMenuItems(item.subMenuItems, isLoggedIn);
          if (!visibleSubs.length) {
            return null;
          }
          const isGroupOpen = openGroups.has(item.value);
          const isGroupActive = visibleSubs.some((sub) => location.startsWith(sub.href as string));
          const GroupIcon = item.icon;

          return (
            <div key={item.value} className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() =>
                  setOpenGroups((current) => {
                    const next = new Set(current);
                    if (next.has(item.value)) {
                      next.delete(item.value);
                    } else {
                      next.add(item.value);
                    }
                    return next;
                  })
                }
                className={cn(
                  "relative flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-transparent bg-transparent px-3 py-2 text-sm font-medium transition-colors hover:bg-background hover:border-border",
                  (isGroupOpen || isGroupActive) && "bg-background border-border"
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <div className="relative flex size-[16px] shrink-0 items-center justify-center overflow-hidden [&_svg]:h-full [&_svg]:w-full [&_svg]:max-h-full [&_svg]:max-w-full">
                    {GroupIcon ? <GroupIcon className="size-full shrink-0" /> : null}
                  </div>
                  {item.label}
                </div>
                <SidebarRowChevron isOpen={isGroupOpen} />
              </button>
              {isGroupOpen &&
                visibleSubs.map((sub) => (
                  <SidebarToolMenuRow
                    key={sub.value}
                    item={sub}
                    location={location}
                    registerRowRef={registerRowRef}
                    nested
                  />
                ))}
            </div>
          );
        }

        return (
          <SidebarToolMenuRow
            key={item.value}
            item={item}
            location={location}
            registerRowRef={registerRowRef}
          />
        );
      })}
    </div>
  );
}

/** Chevron for collapsible sidebar rows — matches Cloud / Intelligence section arrows. */
function SidebarRowChevron({ isOpen }: { isOpen: boolean }) {
  return (
    <div className="relative shrink-0 size-[20px] overflow-clip text-foreground">
      <div className="absolute flex inset-0 items-center justify-center" style={{ containerType: "size" }}>
        <div
          className={cn(
            "flex-none h-[100cqh] w-[100cqw] transition-transform -scale-x-100",
            isOpen && "-rotate-180"
          )}
        >
          <div className="relative size-full overflow-clip">
            <div className="absolute left-[6px] top-[8px] h-[4px] w-[8px]">
              <div className="absolute inset-[-29.46%_-14.73%_-20.83%_-14.73%]">
                <SidebarChevronIcon className="block max-w-none size-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type SidebarSectionRowProps = {
  icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  /** Takes priority over the `icon` component; uses `currentColor`. */
  sidebarIcon?: React.ComponentType<{ className?: string }>;
  /** Arbitrary icon node (e.g. user avatar) — takes priority over sidebarIcon/icon. */
  iconNode?: React.ReactNode;
  label: string;
  /** If provided the row renders as a link, otherwise a button. */
  href?: string;
  isActive?: boolean;
  /** Adds a rotating chevron on the right — for collapsible sections. */
  isOpen?: boolean;
  showChevron?: boolean;
  onClick?: () => void;
};

function SidebarSectionRow({
  icon: Icon,
  iconClassName,
  sidebarIcon: SidebarIcon,
  iconNode,
  label,
  href,
  isActive = false,
  isOpen = false,
  showChevron = false,
  onClick,
}: SidebarSectionRowProps) {
  const rowClass = cn(
    "flex w-full items-center justify-between rounded-lg border border-transparent bg-transparent px-3 py-2 text-sm font-medium transition-colors hover:bg-background hover:border-border",
    isActive && "bg-background border-border"
  );

  const inner = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="relative flex shrink-0 items-center justify-center overflow-hidden text-foreground">
          {iconNode ?? (SidebarIcon ? (
            <SidebarIcon className={cn("block max-w-none object-contain shrink-0 h-[14px]", iconClassName)} />
          ) : Icon ? (
            <Icon className={cn("shrink-0", iconClassName)} />
          ) : null)}
        </div>
        <span className="truncate" title={label}>{label}</span>
      </div>
      {showChevron && <SidebarRowChevron isOpen={isOpen} />}
    </>
  );

  if (href) {
    return (
      <Link href={href}>
        <a className={rowClass}>{inner}</a>
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={rowClass}>
      {inner}
    </button>
  );
}

type CompactNavButtonProps = {
  isActive?: boolean;
  href?: string;
  onClick?: () => void;
  label: string;
  children: React.ReactNode;
  /** Extra classes forwarded to the root element. */
  className?: string;
  /** When set, wraps the control in `SimpleTooltip` (collapsed sidebar nav). */
  tooltipId?: string;
};

/**
 * Square icon button used in the compact (collapsed) sidebar rail.
 */
function CompactNavButton({
  isActive = false,
  href,
  onClick,
  label,
  children,
  className,
  tooltipId,
}: CompactNavButtonProps) {
  const cls = cn(
    "relative flex size-[44px] shrink-0 items-center justify-center rounded-[8px] bg-transparent text-gray-dark-2200 transition-colors",
    "hover:shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]",
    "border border-transparent hover:border-border hover:bg-background",
    "[&_svg]:w-[18px] [&_svg]:max-h-full [&_svg]:max-w-full",
    isActive && "shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)] border-border bg-background",
    className
  );
  const control = href ? (
    <Link href={href}>
      <a className={cls} aria-label={label} title={tooltipId ? undefined : label}>
        {children}
      </a>
    </Link>
  ) : (
    <button
      type="button"
      onClick={onClick}
      className={cls}
      aria-label={label}
      title={tooltipId ? undefined : label}
    >
      {children}
    </button>
  );

  if (!tooltipId) {
    return control;
  }

  return (
    <SimpleTooltip id={tooltipId} content={label} place="right" className="flex shrink-0">
      {control}
    </SimpleTooltip>
  );
}

function CollapseIcon({ className }: { className?: string }) {
  return (
    <div className={`relative overflow-hidden ${className ?? "size-[20px]"}`}>
      {/* Outer box — the "window" frame */}
      <div className="absolute left-[2px] top-[2px] size-[16px] rounded-[4px] border border-[#737373] bg-[#f9f9fb] dark:border-gray-dark-1200 dark:bg-gray-dark-900" />
      {/* Left panel strip — the "sidebar" indicator */}
      <div className="absolute left-[2px] top-[2px] h-[16px] w-[6px] rounded-bl-[4px] rounded-tl-[4px] bg-[#737373] dark:bg-gray-dark-1000" />
    </div>
  );
}

type LeftSidebarProps = {
  className?: string;
  /** Mobile sheet: closes the nav drawer. Omitted on desktop — collapse uses `toggleSidebar` instead. */
  onCloseRequest?: () => void;
};

export const LeftSidebar: React.FC<LeftSidebarProps> = ({ className, onCloseRequest }) => {
  const [location, setLocation] = useLocation();
  const isDashboardActive = isCloudDashboardPath(location);
  const profile = useAuthState((state) => state.profile);
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");
  const updateTheme = useUserStore((state) => state.updateTheme);
  const getToolOptions = useMainStore((state) => state.getToolOptions);
  const sidebarCollapsed = useMainStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useMainStore((state) => state.toggleSidebar);

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    bottom: number;
    left: number;
    width?: number;
    maxHeight?: number;
  } | null>(null);

  const [isPurchaseCreditsModalOpen, setIsPurchaseCreditsModalOpen] = useState(false);
  const [isRequestCreditsModalOpen, setIsRequestCreditsModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const userId = useUserStore((store) => store.userId);
  const balances = useUserStore((store) => store.balances);
  const fetchBalances = useUserStore((store) => store.fetchBalances);

  useEffect(() => {
    if (isLoggedIn && userId) {
      void fetchBalances(true);
    }
  }, [isLoggedIn, userId, fetchBalances]);

  useEffect(() => {
    if (!isExplorerOnlyMode()) {
      updateTheme("light");
    }
  }, [updateTheme]);

  const creditsBalance = useMemo(() => {
    const raw = balances?.[BalanceType.CREDITS];
    return typeof raw === "number" ? toLocaleString(raw, 2) : null;
  }, [balances]);

  const handleOpenCreditsModal = () => {
    trackEvent(
      env.FEATUREFLAG_PURCHASE_CREDITS_ENABLED
        ? AnalyticsEventType.PURCHASE_CREDIT_CLICKED
        : AnalyticsEventType.REQUEST_CREDIT_CLICKED
    );
    if (env.FEATUREFLAG_PURCHASE_CREDITS_ENABLED) {
      setIsPurchaseCreditsModalOpen(true);
    } else {
      setIsRequestCreditsModalOpen(true);
    }
  };
  const profileButtonRef = useRef<HTMLDivElement>(null);
  const { logout } = useAuthActions();

  const displayName = String(getUserName(profile) ?? "");
  const userPicture = getUserPicture(profile);
  const userEmail = String(getUserAttribute(profile, "email") ?? "");

  const openProfileMenu = () => {
    if (profileButtonRef.current) {
      const rect = profileButtonRef.current.getBoundingClientRect();
      const gap = 8;
      const vh = window.innerHeight;
      const margin = 16;

      // Collapsed rail: fly out to the right of the compact button.
      // Expanded sidebar (desktop or mobile sheet): open upward above the profile row.
      const isCollapsedRail = sidebarCollapsed && !onCloseRequest;

      if (isCollapsedRail) {
        setMenuPos({
          bottom: vh - rect.bottom,
          left: rect.right + gap,
          maxHeight: rect.bottom - margin,
        });
      } else {
        setMenuPos({
          bottom: vh - rect.top + gap,
          left: rect.left,
          width: rect.width,
          maxHeight: rect.top - gap - margin,
        });
      }
    }
    setProfileMenuOpen((o) => !o);
  };


  // Close the profile menu whenever the route changes (e.g. after navigation).
  useEffect(() => {
    setProfileMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    await logout();
  };

  const handleNotificationsClick = () => {
    setProfileMenuOpen(false);
    const btn = document.querySelector<HTMLButtonElement>(
      '[data-testid="UserNotificationsButton_trigger"]'
    );
    btn?.click();
  };

  const handleOpenSupport = () => {
    setProfileMenuOpen(false);
    setIsSupportModalOpen(true);
  };

  const resultCount = usePaginatedUserNotificationStore((state) => state.resultCount);

  const sidebarTools = useMemo(() => {
    const options = getToolOptions();
    return LEFT_SIDEBAR_TOOL_ORDER.map((value) => options.find((t) => t.value === value)).filter(
      (t): t is Tool => Boolean(t)
    );
  }, [getToolOptions, location, isLoggedIn]);

  const idToolMenuItems = useMemo(() => {
    const idTool = TOOL_OPTIONS.find((t) => t.value === "id");
    return idTool?.menuItems ?? [];
  }, []);

  const cloudTool = sidebarTools.find((t) => t.value === "cloud");

  const homeMenuItem = cloudTool?.menuItems?.find((m) => m.value === "home");

  /** Derive which tool section the current location belongs to. */
  const activeToolSection = useMemo(() => {
    for (const tool of sidebarTools) {
      if (isSidebarToolSectionActive(tool, location)) {
        return tool.value;
      }
    }
    return null;
  }, [sidebarTools, location]);

  /** At most one tool nav accordion (Cloud, AI, …) open at a time. */
  const [openToolSection, setOpenToolSection] = useState<string | null>(
    () => activeToolSection ?? "cloud"
  );

  /** Auto-expand the section matching the active route on navigation. */
  useEffect(() => {
    if (activeToolSection) {
      setOpenToolSection(activeToolSection);
    }
  }, [activeToolSection]);

  /** Desktop only — mobile sheet always shows the full sidebar. */
  if (sidebarCollapsed && !onCloseRequest) {
    return (
      <aside
        className={cn(
          "flex h-full w-full flex-shrink-0 flex-col overflow-hidden bg-container pb-4",
          className
        )}
      >
        <RequestCreditsModal
          isOpen={isRequestCreditsModalOpen}
          onClose={() => setIsRequestCreditsModalOpen(false)}
        />
        <PurchaseCreditsModal
          isOpen={isPurchaseCreditsModalOpen}
          onClose={() => setIsPurchaseCreditsModalOpen(false)}
        />
        <SupportModal
          isOpen={isSupportModalOpen}
          onClose={() => setIsSupportModalOpen(false)}
        />
        {/* Collapse toggle — fixed-height row matching the expanded logo row */}
        <div className="flex h-[68px] shrink-0 items-center justify-center px-[12px]">
          <CompactNavButton label="Expand sidebar" onClick={() => toggleSidebar()}>
            <CollapseIcon />
          </CompactNavButton>
        </div>

        <div className="flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto px-[12px]">
          <CompactNavButton
            href="/cloud/dashboard"
            label="Dashboard"
            isActive={isDashboardActive}
            tooltipId="sidebar-compact-dashboard"
          >
            <SidebarDashboardIcon className="size-full shrink-0" />
          </CompactNavButton>

          {sidebarTools.map((tool) => {
            const routePrefix = toolRoutePrefix(tool);
            const sectionHref = tool.href ?? `${routePrefix}/`;
            const navItems = getSidebarToolNavItems(tool, cloudTool, homeMenuItem, isLoggedIn);
            const isToolActive = isSidebarToolSectionActive(tool, location);
            const activeNavItem = isToolActive ? getActiveSidebarNavItem(navItems, location, isLoggedIn) : null;
            const ActiveNavIcon = activeNavItem?.icon;
            const SidebarBrandIcon = tool.sidebarIcon;

            return (
              <Fragment key={tool.value}>
                <CompactNavButton
                  href={sectionHref}
                  label={tool.label}
                  isActive={isToolActive}
                  tooltipId={`sidebar-compact-${tool.value}`}
                >
                  {SidebarBrandIcon ? (
                    <SidebarBrandIcon
                      className={cn(
                        "block max-w-none h-[16px] shrink-0 object-contain",
                        tool.value === "cloud" && "text-[#3179FF]",
                        tool.value === "ai" && "text-[#972EF9]"
                      )}
                    />
                  ) : null}
                </CompactNavButton>

                {activeNavItem ? (
                  <CompactNavButton
                    key={`${tool.value}-${activeNavItem.value}`}
                    href={activeNavItem.href as string}
                    label={activeNavItem.label}
                    isActive
                    tooltipId={`sidebar-compact-${tool.value}-${activeNavItem.value}`}
                    onClick={() => {
                      if (activeNavItem.analyticEvent) {
                        trackEvent(activeNavItem.analyticEvent, { tab: activeNavItem.label });
                      }
                    }}
                  >
                    {ActiveNavIcon ? (
                      <div className="relative flex size-[18px] items-center justify-center overflow-hidden [&_svg]:h-full [&_svg]:w-full [&_svg]:max-h-full [&_svg]:max-w-full">
                        <ActiveNavIcon className="size-full shrink-0" />
                      </div>
                    ) : (
                      <span className="px-0.5 text-center text-[9px] font-medium leading-tight">
                        {activeNavItem.label.slice(0, 3)}
                      </span>
                    )}
                  </CompactNavButton>
                ) : null}
              </Fragment>
            );
          })}
        </div>

        <div className="flex shrink-0 flex-col items-center gap-2 px-[12px] pt-4">
          <div ref={profileButtonRef}>
            <CompactNavButton
              onClick={openProfileMenu}
              label={displayName || userEmail || "Account"}
              className="p-[3px] bg-background !border-background hover:!border-background shadow-[0px_2px_8px_0px_rgba(0,0,0,0.16)]"
              isActive={profileMenuOpen}
            >
              {userPicture ? (
                <img src={userPicture} alt="" className="size-[30px] rounded-full object-cover" />
              ) : (
                <div className="flex size-full min-h-0 min-w-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium">
                  {displayName ? displayName.charAt(0).toUpperCase() : "?"}
                </div>
              )}
            </CompactNavButton>
          </div>
        </div>

        {profileMenuOpen && menuPos && (
          <>
            {/* Backdrop — position:fixed escapes overflow:hidden; closes on outside click */}
            <div
              className="fixed inset-0 z-[199]"
              onClick={() => setProfileMenuOpen(false)}
            />
            {/* Menu panel — above the backdrop */}
            <div
              style={{
                bottom: menuPos.bottom,
                left: menuPos.left,
                ...(menuPos.maxHeight != null ? { maxHeight: menuPos.maxHeight } : {}),
                ...(menuPos.width != null ? { width: menuPos.width } : { minWidth: 220 }),
              }}
              className="fixed z-[200] overflow-hidden rounded-xl border border-border bg-background shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)]"
            >
              <div className="overflow-y-auto p-1">
                {idToolMenuItems.map((item) => {
                  if (!item.href) return null;
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.value}
                      onClick={() => {
                        setProfileMenuOpen(false);
                        setLocation(explorerPrefixedPath(item.href as string));
                      }}
                      className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                    >
                      <span className="mr-3 flex h-[18px] w-[18px] shrink-0 items-center justify-center text-muted-foreground">
                        {ItemIcon && <ItemIcon className="h-full w-full" />}
                      </span>
                      {item.label}
                    </button>
                  );
                })}

                <div className="mx-2 my-1 h-px bg-border" />

                <button
                  onClick={handleNotificationsClick}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <div className="flex items-center">
                    <Bell className="mr-3 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                    Notifications
                  </div>
                  {resultCount ? (
                    <div className="relative ml-2 flex shrink-0 items-center">
                      <span className="rounded-md border border-border px-1.5 py-0.5 text-xs font-medium leading-none">
                        {resultCount}
                      </span>
                      <span className="absolute -right-1 -top-1 size-2 rounded-full bg-red-500" />
                    </div>
                  ) : null}
                </button>

                <div className="mx-2 my-1 h-px bg-border" />

                <button
                  onClick={handleOpenSupport}
                  className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <QuestionIcon className="mr-3 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                  Support
                </button>

                <div className="mx-2 my-1 h-px bg-border" />

                <button
                  onClick={handleLogout}
                  className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <LogOutIcon className="mr-3 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                  Log Out
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        "flex h-full w-full flex-shrink-0 flex-col overflow-hidden bg-container px-4",
        className
      )}
    >
      <RequestCreditsModal
        isOpen={isRequestCreditsModalOpen}
        onClose={() => setIsRequestCreditsModalOpen(false)}
      />
      <PurchaseCreditsModal
        isOpen={isPurchaseCreditsModalOpen}
        onClose={() => setIsPurchaseCreditsModalOpen(false)}
      />
      <SupportModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />

      {/* Logo row — matches Figma NavbarTop: h-68px, px-16, space-between */}
      <div className="flex h-[68px] shrink-0 items-center justify-between overflow-hidden">
        <Link href="/cloud/dashboard" asChild>
          <a
            className="inline-flex h-[14px] w-[98px] shrink-0 text-foreground"
            role="img"
            aria-label="io.NET"
          >
            <SidebarLogoIcon className="block h-full w-full max-w-none" />
          </a>
        </Link>
        <button
          type="button"
          onClick={() => {
            if (onCloseRequest) {
              onCloseRequest();
            } else {
              toggleSidebar();
            }
          }}
          className="flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:opacity-70"
          aria-label={onCloseRequest ? "Close navigation menu" : "Collapse sidebar"}
        >
          <CollapseIcon />
        </button>
      </div>

      {/* Scrollable nav area */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        <SidebarSectionRow
          sidebarIcon={SidebarDashboardIcon}
          label="Dashboard"
          href="/cloud/dashboard"
          isActive={isDashboardActive}
          iconClassName="!h-[20px]"
        />

        {sidebarTools.map((tool) => {
          const isToolActive = isSidebarToolSectionActive(tool, location);
          const isOpen = openToolSection === tool.value;
          const SectionIcon = tool.icon;
          const navItems = getSidebarToolNavItems(tool, cloudTool, homeMenuItem, isLoggedIn);

          return (
            <div key={tool.value} className="flex w-full min-w-0 flex-col gap-2">
              <SidebarSectionRow
                sidebarIcon={tool.sidebarIcon}
                icon={SectionIcon ?? undefined}
                iconClassName={cn(
                  tool.value === "cloud" && "text-[#3179FF]",
                  tool.value === "ai" && "text-[#972EF9]"
                )}
                label={tool.label}
                isActive={isToolActive}
                isOpen={isOpen}
                showChevron
                onClick={() =>
                  setOpenToolSection((current) => (current === tool.value ? null : tool.value))
                }
              />

              {isOpen && (
                <div className="flex w-full min-w-0 items-start">
                  <SidebarToolSubmenuList navItems={navItems} location={location} isLoggedIn={isLoggedIn} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom section — always pinned to the bottom */}
      <div className="shrink-0 flex flex-col gap-2 pb-4 pt-4">
        {/* Get Started */}
        {isLoggedIn && <SidebarGetStarted onPurchaseCredits={handleOpenCreditsModal} />}

        {/* Credits balance */}
        {isLoggedIn && getIsCreditsEnabled() && (
          <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium">
            <span className="text-muted-foreground">Credits Balance</span>
            <div className="flex items-center gap-2">
              {creditsBalance !== null && (
                <span className="font-medium text-foreground">${creditsBalance}</span>
              )}
              <button
                type="button"
                onClick={handleOpenCreditsModal}
                className="flex size-[22px] shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent"
                aria-label="Add credits"
              >
                <span className="text-[14px] leading-none">+</span>
              </button>
            </div>
          </div>
        )}

        {/* User profile — only visible when logged in */}
        {isLoggedIn && (
          <div ref={profileButtonRef}>
            <SidebarSectionRow
              iconNode={
                userPicture ? (
                  <img
                    src={userPicture}
                    alt={displayName}
                    className="size-[30px] rounded-full object-cover"
                  />
                ) : (
                  <div className="flex size-[30px] items-center justify-center rounded-full bg-muted text-[10px] font-medium text-foreground">
                    {displayName ? displayName.charAt(0).toUpperCase() : "?"}
                  </div>
                )
              }
              label={displayName || userEmail || "Sign in"}
              showChevron
              isOpen={profileMenuOpen}
              onClick={openProfileMenu}
              isActive={true}
            />
          </div>
        )}

        {profileMenuOpen && menuPos && (
          <>
            {/* Backdrop — position:fixed escapes overflow:hidden; closes on outside click */}
            <div
              className="fixed inset-0 z-[199]"
              onClick={() => setProfileMenuOpen(false)}
            />
            {/* Menu panel — above the backdrop */}
            <div
              style={{
                bottom: menuPos.bottom,
                left: menuPos.left,
                ...(menuPos.maxHeight != null ? { maxHeight: menuPos.maxHeight } : {}),
                ...(menuPos.width != null ? { width: menuPos.width } : { minWidth: 220 }),
              }}
              className="fixed z-[200] overflow-hidden rounded-xl border border-border bg-background shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)]"
            >
              <div className="overflow-y-auto p-1">
                {idToolMenuItems.map((item) => {
                  if (!item.href) return null;
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.value}
                      onClick={() => {
                        setProfileMenuOpen(false);
                        onCloseRequest?.();
                        setLocation(explorerPrefixedPath(item.href as string));
                      }}
                      className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                    >
                      <span className="mr-3 flex h-[18px] w-[18px] shrink-0 items-center justify-center text-muted-foreground">
                        {ItemIcon && <ItemIcon className="h-full w-full" />}
                      </span>
                      {item.label}
                    </button>
                  );
                })}

                <div className="mx-2 my-1 h-px bg-border" />

                {/* <button
                  onClick={handleNotificationsClick}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <div className="flex items-center">
                    <Bell className="mr-3 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                    Notifications
                  </div>
                  {resultCount ? (
                    <div className="relative ml-2 flex shrink-0 items-center">
                      <span className="rounded-md border border-border px-1.5 py-0.5 text-xs font-medium leading-none">
                        {resultCount}
                      </span>
                      <span className="absolute -right-1 -top-1 size-2 rounded-full bg-red-500" />
                    </div>
                  ) : null}
                </button>

                <div className="mx-2 my-1 h-px bg-border" /> */}

                <button
                  onClick={handleOpenSupport}
                  className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <QuestionIcon className="mr-3 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                  Support
                </button>

                <div className="mx-2 my-1 h-px bg-border" />

                <button
                  onClick={handleLogout}
                  className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <LogOutIcon className="mr-3 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                  Log Out
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </aside>
  );
};
